/* ──────────────────────────────────────────────────────────────
 *  useBushingStore — bushing type selection + derived lookups
 *
 *  All downstream UI (target grid, field mapping, sleeve options)
 *  subscribes to `bushingType` and reads the derived config.
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';
import type { BushingType, BushingTypeConfig, ParamName, FieldMapping } from '@/types/bushing';
import { BUSHING_TYPE_CONFIGS } from '@/constants/bushingTypes';
import { DETAILS_FIELD_MAPPINGS } from '@/constants/fieldMappings';

interface BushingState {
  /** Currently selected bushing type. */
  bushingType: BushingType;

  /* ── Derived (computed from bushingType) ───────────────── */
  config: BushingTypeConfig;
  paramNames: ParamName[];
  displayLabels: BushingTypeConfig['displayLabels'];
  detailsFieldMapping: FieldMapping;

  /* ── Actions ──────────────────────────────────────────── */
  setBushingType: (type: BushingType) => void;
}

/** Helper — derive all dependent values from a bushing type. */
function deriveFromType(type: BushingType) {
  const config = BUSHING_TYPE_CONFIGS[type];
  return {
    config,
    paramNames: config.paramNames,
    displayLabels: config.displayLabels,
    detailsFieldMapping: DETAILS_FIELD_MAPPINGS[type],
  };
}

export const useBushingStore = create<BushingState>()((set) => ({
  bushingType: 'A1',
  ...deriveFromType('A1'),

  setBushingType: (type) =>
    set({
      bushingType: type,
      ...deriveFromType(type),
    }),
}));
